import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Menu,
  MenuList,
  MenuItem as MenuListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CardActions,
  Rating,
  Tooltip,
  Fab,
  Paper,
  List,
  ListItem,
  ListItemText as MuiListItemText,
  Avatar,
  Badge,
  Autocomplete,
  CircularProgress,
  Skeleton,
  LinearProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  FlightTakeoff as FlightIcon,
  Hotel as HotelIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  GetApp as ExportIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Schedule as ScheduleIcon,
  Restaurant as RestaurantIcon,
  DirectionsBus as BusIcon,
  PhotoCamera as PhotoIcon,
  LocalOffer as OfferIcon,
  CardTravel as TravelIcon,
  Language as PublicIcon,
  Business as BusinessIcon,
  PersonPin as PersonPinIcon,
  Insights as InsightsIcon,
  Assignment as AssignmentIcon,
  CardGiftcard,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// Import Settings Context for real-time data
import { useSettings } from '../../contexts/SettingsContext';
import { usePackagesData, useClientsData, useReservationsData, useSuppliersData } from '../../services/dataService';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Add these image constants at the top of the file after imports
const packageImages = {
  'Istanbul': '/images/forfaits/istanbul.jpg',
  'Dubaï': '/images/forfaits/dubai.jpg',
  'Paris': '/images/forfaits/paris.jpg',
  'La Mecque': '/images/forfaits/mecca.jpg',
  'Marrakech': '/images/forfaits/marrakech.jpg',
  'Rome': '/images/forfaits/rome.jpg',
  'Istanbul, Turquie': '/images/forfaits/istanbul.jpg',
  'Dubaï, EAU': '/images/forfaits/dubai.jpg',
  'Paris, France': '/images/forfaits/paris.jpg',
  'La Mecque, Arabie Saoudite': '/images/forfaits/mecca.jpg',
  'Marrakech, Maroc': '/images/forfaits/marrakech.jpg',
  'Rome, Italie': '/images/forfaits/rome.jpg',
  'Culturel': '/images/forfaits/culturel.jpg',
  'Luxe': '/images/forfaits/luxe.jpg',
  'Religieux': '/images/forfaits/religieux.jpg',
  'Aventure': '/images/forfaits/aventure.jpg',
  'Business': '/images/forfaits/business.jpg',
  'Familial': '/images/forfaits/familial.jpg'
};

// Helper function to get image for a package
const getPackageImage = (forfait) => {
  // First check if there's a custom image URL
  if (forfait.image && forfait.image.trim() !== '') return forfait.image;
  
  // Then try to get image by destination
  if (packageImages[forfait.destination]) return packageImages[forfait.destination];
  
  // Then try to get by category
  if (packageImages[forfait.categorie]) return packageImages[forfait.categorie];
  
  // Try to get by first part of destination
  const destination = forfait.destination?.split(',')[0];
  if (packageImages[destination]) return packageImages[destination];
  
  // Default image
  return '/images/forfaits/default.jpg';
};

// Add a component for drag and drop image upload
const ImageDropzone = ({ value, onChange }) => {
  const [previewUrl, setPreviewUrl] = useState(value || '');
  const [isDragging, setIsDragging] = useState(false);
  
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files) {
      setIsDragging(true);
    }
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      handleFile(file);
    }
  }, []);
  
  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFile(file);
    }
  };
  
  const handleFile = (file) => {
    // Check if file is an image
    if (!file.type.match('image.*')) {
      alert('Veuillez sélectionner une image');
      return;
    }
    
    // Convert to base64 for preview and storage
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Image = e.target.result;
      setPreviewUrl(base64Image);
      onChange(base64Image);
    };
    reader.readAsDataURL(file);
  };
  
  // Completely separate handler for delete button
  const handleDeleteClick = () => {
    setPreviewUrl('');
    onChange('');
  };
  
  // If we have a preview, show the image with delete button
  if (previewUrl) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Image du forfait</Typography>
        <Box sx={{ position: 'relative', mb: 2 }}>
          <img 
            src={previewUrl} 
            alt="Aperçu" 
            style={{ 
              width: '100%', 
              height: '200px', 
              objectFit: 'cover',
              borderRadius: '8px'
            }} 
          />
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            sx={{ 
              position: 'absolute', 
              top: 8, 
              right: 8,
            }}
          >
            Supprimer
          </Button>
        </Box>
      </Box>
    );
  }
  
  // Otherwise show the dropzone
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Image du forfait</Typography>
      <Box
        sx={{
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          bgcolor: isDragging ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
          transition: 'all 0.2s',
          cursor: 'pointer'
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById('image-upload').click()}
      >
        <input
          type="file"
          id="image-upload"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileInput}
        />
        <PhotoIcon sx={{ fontSize: 40, color: 'grey.500', mb: 1 }} />
        <Typography variant="body1">
          Glissez une image ici ou cliquez pour sélectionner
        </Typography>
        <Typography variant="body2" color="text.secondary">
          JPG, PNG ou GIF • Max 5MB
        </Typography>
      </Box>
    </Box>
  );
};

const Forfaits = () => {
  // Get settings context for real-time updates
  const { settings, formatCurrency, formatDate, calculateTotalWithTax } = useSettings();
  
  // Fetch interconnected data
  const { packages, loading: packagesLoading, error: packagesError, stats: packagesStats } = usePackagesData();
  const { clients, loading: clientsLoading } = useClientsData();
  const { reservations, loading: reservationsLoading } = useReservationsData();
  const { suppliers, loading: suppliersLoading } = useSuppliersData();
  
  // Combined loading state
  const isLoading = packagesLoading || clientsLoading || reservationsLoading || suppliersLoading;
  
  // Local state
  const [filteredForfaits, setFilteredForfaits] = useState([]);
  const [packageStats, setPackageStats] = useState({
    total: 0,
    active: 0,
    reservations: 0,
    interestedClients: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('all');
  const [filterPrix, setFilterPrix] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingForfait, setEditingForfait] = useState(null);
  // Add new state for details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [forfaitDetails, setForfaitDetails] = useState(null);
  const [formData, setFormData] = useState({
    titre: '',
    destination: '',
    categorie: 'Culturel',
    prix: 0,
    duree: '',
    statut: 'Actif',
    description: '',
    fournisseurId: '',
    image: '', // Add image field
    inclusions: [] // Keep inclusions, remove clientsInteresses
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedForfait, setSelectedForfait] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  
  // Add state for confirmation dialog
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    forfaitId: null,
    hasReservations: false
  });

  // Function to force refresh all data
  const refreshAllData = useCallback(() => {
    // Clear cache to force reload
    localStorage.removeItem('dataCache');
    
    // Dispatch events to notify other components
    window.dispatchEvent(new CustomEvent('packageDataUpdate'));
    window.dispatchEvent(new CustomEvent('supplierDataUpdate'));
    window.dispatchEvent(new CustomEvent('reservationDataUpdate'));
    window.dispatchEvent(new CustomEvent('clientDataUpdate'));
    window.dispatchEvent(new CustomEvent('financialDataUpdate'));
    
    setSnackbar({
      open: true,
      message: 'Toutes les données ont été actualisées',
      severity: 'success'
    });
  }, []);

  // Add event listeners for real-time data updates from other pages
  useEffect(() => {
    // Function to handle storage events (localStorage changes)
    const handleStorageChange = (e) => {
      if (e.key === 'packagesData') {
        // Force reload packages data
        const updatedPackages = JSON.parse(localStorage.getItem('packagesData') || '[]');
        
        // Let the main useEffect handle filtering
        setSnackbar({
          open: true,
          message: 'Données des forfaits mises à jour en temps réel',
          severity: 'info'
        });
      } else if (e.key === 'suppliersData') {
        // Refresh supplier data as it might affect package relationships
        setSnackbar({
          open: true,
          message: 'Fournisseurs mis à jour - Relations forfaits actualisées',
          severity: 'info'
        });
        
        // If a package details dialog is open, refresh its data
        if (detailsDialogOpen && forfaitDetails) {
          handleOpenDetailsDialog(forfaitDetails);
        }
      } else if (e.key === 'reservationsData' || e.key === 'clientsData') {
        // For related data changes, show a notification
        setSnackbar({
          open: true,
          message: 'Données associées mises à jour - Interconnexion active',
          severity: 'info'
        });
        
        // If a package details dialog is open, refresh its data
        if (detailsDialogOpen && forfaitDetails) {
          handleOpenDetailsDialog(forfaitDetails);
        }
      }
    };

    // Function to handle custom events from other components
    const handlePackageEventUpdate = (e) => {
      setSnackbar({
        open: true,
        message: 'Données des forfaits mises à jour en temps réel',
        severity: 'info'
      });
      
      // If a package details dialog is open, refresh its data
      if (detailsDialogOpen && forfaitDetails) {
        const updatedPackages = JSON.parse(localStorage.getItem('packagesData') || '[]');
        const updatedPackage = updatedPackages.find(p => p.id === forfaitDetails.id);
        if (updatedPackage) {
          handleOpenDetailsDialog(updatedPackage);
        }
      }
    };

    const handleSupplierEventUpdate = (e) => {
      setSnackbar({
        open: true,
        message: 'Fournisseurs mis à jour - Relations forfaits actualisées',
        severity: 'info'
      });
      
      // If a package details dialog is open, refresh its data
      if (detailsDialogOpen && forfaitDetails) {
        handleOpenDetailsDialog(forfaitDetails);
      }
    };

    const handleReservationEventUpdate = (e) => {
      setSnackbar({
        open: true,
        message: 'Réservations mises à jour - Relations forfaits actualisées',
        severity: 'info'
      });
      
      // If a package details dialog is open, refresh its data
      if (detailsDialogOpen && forfaitDetails) {
        handleOpenDetailsDialog(forfaitDetails);
      }
    };
    
    const handleClientEventUpdate = (e) => {
      setSnackbar({
        open: true,
        message: 'Clients mis à jour - Relations forfaits actualisées',
        severity: 'info'
      });
      
      // If a package details dialog is open, refresh its data
      if (detailsDialogOpen && forfaitDetails) {
        handleOpenDetailsDialog(forfaitDetails);
      }
    };

    // Add event listeners
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('packageDataUpdate', handlePackageEventUpdate);
    window.addEventListener('supplierDataUpdate', handleSupplierEventUpdate);
    window.addEventListener('reservationDataUpdate', handleReservationEventUpdate);
    window.addEventListener('clientDataUpdate', handleClientEventUpdate);

    // Clean up event listeners
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('packageDataUpdate', handlePackageEventUpdate);
      window.removeEventListener('supplierDataUpdate', handleSupplierEventUpdate);
      window.removeEventListener('reservationDataUpdate', handleReservationEventUpdate);
      window.removeEventListener('clientDataUpdate', handleClientEventUpdate);
    };
  }, [detailsDialogOpen, forfaitDetails]);
  
  // Connect packages with clients and reservations
  useEffect(() => {
    if (!packagesLoading && !clientsLoading && !reservationsLoading && !suppliersLoading) {
      console.log("Forfaits component received data:");
      console.log("Packages with existing relationships:", packages);
      
      // Packages should already have client and reservation relationships 
      // from usePackagesData hook and resetAndInitializeData
      const enhancedPackages = packages.map(forfait => {
        // These should already exist from usePackagesData
        const relatedReservations = forfait.reservations || [];
        const relatedClients = forfait.clients || [];
        
        return {
          ...forfait,
          // Ensure these fields exist for UI compatibility
          reservations: relatedReservations,
          clientsInteresses: relatedClients,
          popularityScore: (relatedReservations.length * 10) + (relatedClients.length * 5)
        };
      });
      
      // Calculate stats directly from the enhanced packages
      const totalPackages = enhancedPackages.length;
      const activePackages = enhancedPackages.filter(pkg => pkg.statut === 'Actif').length;
      const totalReservations = reservations.length;
      
      // Count unique clients from client relationships
      const allInterestedClientIds = new Set();
      enhancedPackages.forEach(pkg => {
        const clientArray = pkg.clientsInteresses || pkg.clients || [];
        clientArray.forEach(client => {
          if (client && client.id) {
            allInterestedClientIds.add(Number(client.id));
          }
        });
      });
      
      console.log("All interested clients:", allInterestedClientIds);
      
      // Update stats in local state to display in UI
      setPackageStats({
        total: totalPackages,
        active: activePackages,
        reservations: totalReservations,
        interestedClients: allInterestedClientIds.size
      });
      
      setFilteredForfaits(enhancedPackages);
    }
  }, [packages, clients, reservations, packagesLoading, clientsLoading, reservationsLoading, suppliersLoading]);
  
  // Filter packages based on search and filters
  useEffect(() => {
    if (!packagesLoading) {
      let filtered = [...packages];
      
      // Apply search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(f => 
          f.titre.toLowerCase().includes(search) || 
          f.destination.toLowerCase().includes(search) ||
          f.categorie.toLowerCase().includes(search)
        );
      }
      
      // Apply category filter
      if (filterCategorie !== 'all') {
        filtered = filtered.filter(f => f.categorie === filterCategorie);
      }
      
      // Apply price filter
      if (filterPrix !== 'all') {
        switch (filterPrix) {
          case 'under50k':
            filtered = filtered.filter(f => f.prix < 50000);
            break;
          case '50k-100k':
            filtered = filtered.filter(f => f.prix >= 50000 && f.prix <= 100000);
            break;
          case '100k-150k':
            filtered = filtered.filter(f => f.prix > 100000 && f.prix <= 150000);
            break;
          case 'over150k':
            filtered = filtered.filter(f => f.prix > 150000);
            break;
          default:
            break;
        }
      }
      
      setFilteredForfaits(filtered);
    }
  }, [searchTerm, filterCategorie, filterPrix, packages, packagesLoading]);

  const handleOpenDialog = (forfait = null) => {
    if (forfait) {
      setEditingForfait(forfait);
      setFormData({
        titre: forfait.titre || '',
        destination: forfait.destination || '',
        categorie: forfait.categorie || 'Culturel',
        prix: forfait.prix || 0,
        duree: forfait.duree || '',
        statut: forfait.statut || 'Actif',
        description: forfait.description || '',
        fournisseurId: forfait.fournisseurId || '',
        image: forfait.image || '', // Add image field
        inclusions: forfait.inclusions || [] // Keep inclusions, remove clientsInteresses
      });
    } else {
      setEditingForfait(null);
      setFormData({
        titre: '',
        destination: '',
        categorie: 'Culturel',
        prix: 0,
        duree: '',
        statut: 'Actif',
        description: '',
        fournisseurId: '',
        image: '', // Add image field
        inclusions: [] // Keep inclusions, remove clientsInteresses
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingForfait(null);
    setFormData({
      titre: '',
      destination: '',
      categorie: 'Culturel',
      prix: 0,
      duree: '',
      statut: 'Actif',
      description: '',
      fournisseurId: '',
      image: '', // Add image field
      inclusions: [] // Keep inclusions, remove clientsInteresses
    });
  };

  const handleSaveForfait = () => {
    // Get existing packages data from localStorage
    const currentPackages = JSON.parse(localStorage.getItem('packagesData')) || packages || [];
    const currentReservations = JSON.parse(localStorage.getItem('reservationsData')) || [];
    
    if (editingForfait) {
      // Update existing package
      const packageId = Number(editingForfait.id);
      
      // Create updated package with number ID
      const updatedPackage = {
        ...formData,
        id: packageId, // Ensure ID is a number
        statut: formData.statut || 'Actif',
        dateModification: new Date().toISOString().split('T')[0]
      };
      
      // Update the package in the array
      const updatedPackages = currentPackages.map(pkg => 
        Number(pkg.id) === packageId ? updatedPackage : pkg
      );
      
      // Save back to localStorage
      localStorage.setItem('packagesData', JSON.stringify(updatedPackages));
      
      // Check if we need to update any related reservations
      if (formData.titre !== editingForfait.titre) {
        // Package name changed, update any references in reservations
        const updatedReservations = currentReservations.map(res => {
          if (Number(res.forfaitId) === packageId) {
            return {
              ...res,
              forfait: formData.titre
            };
          }
          return res;
        });
        
        // Save updated reservations
        localStorage.setItem('reservationsData', JSON.stringify(updatedReservations));
      }
      
      // Trigger refresh
      window.dispatchEvent(new Event('storage'));
      
      setSnackbar({
        open: true,
        message: 'Forfait mis à jour avec succès!',
        severity: 'success'
      });
    } else {
      // Create a new package
      const newId = Date.now();
      
      // Create new package with number ID
      const newPackage = {
        ...formData,
        id: newId, // Ensure ID is a number
        dateCreation: new Date().toISOString().split('T')[0],
        statut: formData.statut || 'Actif'
      };
      
      const updatedPackages = [...currentPackages, newPackage];
      
      // Save back to localStorage
      localStorage.setItem('packagesData', JSON.stringify(updatedPackages));
      
      // Trigger refresh
      window.dispatchEvent(new Event('storage'));
      
      setSnackbar({
        open: true,
        message: 'Nouveau forfait créé avec succès!',
        severity: 'success'
      });
    }
    
    handleCloseDialog();
  };

  const handleDeleteForfait = (forfaitId) => {
    // Ensure ID is a number
    const packageId = Number(forfaitId);
    
    // Get current packages data
    const currentPackages = JSON.parse(localStorage.getItem('packagesData')) || packages || [];
    const currentReservations = JSON.parse(localStorage.getItem('reservationsData')) || [];
    
    // Check if package has associated reservations
    const hasReservations = currentReservations.some(res => Number(res.forfaitId) === packageId);
    
    if (hasReservations) {
      // Open confirmation dialog instead of showing warning
      setDeleteConfirmDialog({
        open: true,
        forfaitId: packageId,
        hasReservations: true
      });
      
      // Close the menu if open
      if (anchorEl) {
        setAnchorEl(null);
        setSelectedForfait(null);
      }
      
      return;
    }
    
    // If no reservations, open normal confirmation
    setDeleteConfirmDialog({
      open: true,
      forfaitId: packageId,
      hasReservations: false
    });
    
    // Close the menu if open
    if (anchorEl) {
      setAnchorEl(null);
      setSelectedForfait(null);
    }
  };
  
  // Function to confirm deletion
  const confirmDeleteForfait = () => {
    const packageId = deleteConfirmDialog.forfaitId;
    
    // Get current packages and reservations data
    const currentPackages = JSON.parse(localStorage.getItem('packagesData')) || packages || [];
    const currentReservations = JSON.parse(localStorage.getItem('reservationsData')) || reservations || [];
    
    // Filter out the deleted package
    const updatedPackages = currentPackages.filter(pkg => Number(pkg.id) !== packageId);
    
    // Handle associated reservations based on user choice
    if (deleteConfirmDialog.hasReservations) {
      // Option 1: Delete associated reservations
      const updatedReservations = currentReservations.filter(res => Number(res.forfaitId) !== packageId);
      localStorage.setItem('reservationsData', JSON.stringify(updatedReservations));
      
      // Option 2: Mark reservations as cancelled (alternative approach)
      // const updatedReservations = currentReservations.map(res => {
      //   if (Number(res.forfaitId) === packageId) {
      //     return { ...res, statut: 'Annulée', statutPaiement: 'Remboursé' };
      //   }
      //   return res;
      // });
      // localStorage.setItem('reservationsData', JSON.stringify(updatedReservations));
    }
    
    // Save back to localStorage
    localStorage.setItem('packagesData', JSON.stringify(updatedPackages));
    
    // Trigger refresh for all components to update
    window.dispatchEvent(new Event('storage'));
    
    setSnackbar({
      open: true,
      message: deleteConfirmDialog.hasReservations 
        ? 'Forfait et ses réservations associées supprimés avec succès!' 
        : 'Forfait supprimé avec succès!',
      severity: 'success'
    });
    
    // Close the confirmation dialog
    setDeleteConfirmDialog({
      open: false,
      forfaitId: null,
      hasReservations: false
    });
  };
  
  // Function to cancel deletion
  const cancelDeleteForfait = () => {
    setDeleteConfirmDialog({
      open: false,
      forfaitId: null,
      hasReservations: false
    });
  };

  const handleMenuClick = (event, forfait) => {
    setAnchorEl(event.currentTarget);
    setSelectedForfait(forfait);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedForfait(null);
  };

  const getCategorieColor = (categorie) => {
    switch (categorie) {
      case 'Culturel': return 'primary';
      case 'Luxe': return 'secondary';
      case 'Religieux': return 'success';
      case 'Aventure': return 'warning';
      case 'Business': return 'info';
      case 'Familial': return 'error';
      default: return 'default';
    }
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'Actif': return 'success';
      case 'Inactif': return 'error';
      default: return 'default';
    }
  };
  
  // Get unique categories for filter
  const categories = [...new Set(packages.map(f => f.categorie))];
  
  // If loading, show loading state
  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ mb: 3 }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="text" width={300} height={40} />
          <Skeleton variant="rounded" width={120} height={40} />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Skeleton variant="rounded" width={200} height={56} />
          <Skeleton variant="rounded" width={150} height={56} />
          <Skeleton variant="rounded" width={150} height={56} />
        </Box>
        
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item}>
              <Card sx={{ height: '100%' }}>
                <Skeleton variant="rectangular" height={200} />
                <CardContent>
                  <Skeleton variant="text" height={32} />
                  <Skeleton variant="text" width="60%" />
                  <Box sx={{ mt: 2 }}>
                    <Skeleton variant="text" width="40%" />
                    <Skeleton variant="text" width="70%" />
                  </Box>
                </CardContent>
                <CardActions>
                  <Skeleton variant="rounded" width={80} height={36} />
                  <Skeleton variant="rounded" width={80} height={36} />
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }
  
  // If error, show error state
  if (packagesError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Erreur lors du chargement des forfaits: {packagesError}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
        >
          Réessayer
        </Button>
      </Box>
    );
  }

  // Function to format currency correctly
  const formatCurrencyForPDF = (amount) => {
    // Convert number to string with spaces as thousand separators
    const numberPart = Math.round(amount || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${numberPart} DZD`;
  };

  const exportForfaitsToPDF = () => {
    try {
      // Create a new PDF document
      const doc = new jsPDF('landscape');
      
      // Document dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      
      // Colors
      const primaryColor = [30, 64, 175]; // blue
      const secondaryColor = [124, 58, 237]; // purple
      const successColor = [16, 185, 129]; // green
      const warningColor = [245, 158, 11]; // amber
      const errorColor = [239, 68, 68]; // red
      const textColor = [31, 41, 55]; // dark gray
      
      // Add header
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      // Add agency logo and name
      if (settings.agencyLogo) {
        try {
          // Add the agency logo from base64 string
          doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
          
          // Add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
        } catch (logoError) {
          console.error('Error adding logo to PDF:', logoError);
          // Fallback to text only
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
      } else {
        // No logo, just add agency name
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
      }
      
      // Add title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text("Catalogue des Forfaits", pageWidth - margin, 17, { align: 'right' });
      
      // Add date and filters
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(10);
      doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, margin, 35);
      doc.text(`Filtres: Catégorie: ${filterCategorie === 'all' ? 'Toutes' : filterCategorie} | Prix: ${filterPrix === 'all' ? 'Tous' : filterPrix}`, margin, 42);
      
      // Add summary section
      doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setLineWidth(0.5);
      doc.line(margin, 48, pageWidth - margin, 48);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Résumé des Forfaits", margin, 58);
      
      // Calculate some key metrics
      const totalForfaits = filteredForfaits.length;
      const activeForfaits = filteredForfaits.filter(f => f.statut === 'Actif').length;
      
      // Get categories and their counts
      const categoryCounts = {};
      filteredForfaits.forEach(forfait => {
        if (!categoryCounts[forfait.categorie]) {
          categoryCounts[forfait.categorie] = 0;
        }
        categoryCounts[forfait.categorie]++;
      });
      
      // Calculate average price
      const totalPrice = filteredForfaits.reduce((sum, forfait) => sum + (forfait.prix || 0), 0);
      const averagePrice = totalForfaits > 0 ? totalPrice / totalForfaits : 0;
      
      const stats = [
        { label: "Total des Forfaits", value: `${totalForfaits}` },
        { label: "Forfaits Actifs", value: `${activeForfaits} (${Math.round(activeForfaits / totalForfaits * 100 || 0)}%)` },
        { label: "Prix Moyen", value: formatCurrencyForPDF(averagePrice) },
        { label: "Destinations Disponibles", value: `${new Set(filteredForfaits.map(f => f.destination)).size}` },
        { label: "Nombre de Catégories", value: `${Object.keys(categoryCounts).length}` }
      ];
      
      doc.autoTable({
        startY: 62,
        head: [['Indicateur', 'Valeur']],
        body: stats.map(item => [item.label, item.value]),
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add categories summary
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Répartition par Catégorie", margin, doc.lastAutoTable.finalY + 15);
      
      const categoryStats = Object.entries(categoryCounts)
        .map(([category, count]) => ({
          category,
          count,
          percentage: (count / totalForfaits * 100).toFixed(1)
        }))
        .sort((a, b) => b.count - a.count);
      
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Catégorie', 'Nombre de Forfaits', 'Pourcentage']],
        body: categoryStats.map(item => [
          item.category, 
          item.count.toString(),
          `${item.percentage}%`
        ]),
        theme: 'grid',
        headStyles: { fillColor: secondaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add destinations summary
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Destinations", margin, doc.lastAutoTable.finalY + 15);
      
      const destinationCounts = {};
      filteredForfaits.forEach(f => {
        if (!destinationCounts[f.destination]) {
          destinationCounts[f.destination] = 0;
        }
        destinationCounts[f.destination]++;
      });
      
      const destinationStats = Object.entries(destinationCounts)
        .map(([destination, count]) => ({
          destination,
          count,
          percentage: (count / totalForfaits * 100).toFixed(1)
        }))
        .sort((a, b) => b.count - a.count);
      
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Destination', 'Nombre de Forfaits', 'Pourcentage']],
        body: destinationStats.map(item => [
          item.destination, 
          item.count.toString(),
          `${item.percentage}%`
        ]),
        theme: 'grid',
        headStyles: { fillColor: successColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add forfaits details
      doc.addPage();
      
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, 10, 30, 10, 1, 1, 'F');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("TravelIT", margin + 6, 17);
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.text("Liste des Forfaits", pageWidth - margin, 17, { align: 'right' });
      
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(12);
      doc.text("Détails de tous les forfaits disponibles", margin, 40);
      
      // Add forfaits table
      const forfaitsTableData = filteredForfaits.map(f => [
        f.id,
        f.titre,
        f.destination,
        f.categorie,
        f.duree,
        formatCurrencyForPDF(f.prix),
        f.statut || 'N/A',
        f.description ? (f.description.length > 30 ? f.description.substring(0, 30) + '...' : f.description) : 'N/A'
      ]);
      
      doc.autoTable({
        startY: 50,
        head: [['ID', 'Titre', 'Destination', 'Catégorie', 'Durée', 'Prix', 'Statut', 'Description']],
        body: forfaitsTableData,
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 50 },
          2: { cellWidth: 40 },
          3: { cellWidth: 25 },
          4: { cellWidth: 20 },
          5: { cellWidth: 25 },
          6: { cellWidth: 20 },
          7: { cellWidth: 50 }
        },
        margin: { left: margin, right: margin },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        didDrawPage: function (data) {
          // Add header on each page
          doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
          doc.rect(0, 0, pageWidth, 25, 'F');
          
          // Add agency logo and name
          if (settings.agencyLogo) {
            try {
              // Add the agency logo from base64 string
              doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
              
              // Add agency name
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(12);
              doc.setFont('helvetica', 'bold');
              doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
            } catch (logoError) {
              console.error('Error adding logo to PDF:', logoError);
              // Fallback to text only
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(12);
              doc.setFont('helvetica', 'bold');
              doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
            }
          } else {
            // No logo, just add agency name
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
          }
          
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(16);
          doc.text("Liste des Forfaits", pageWidth - margin, 17, { align: 'right' });
          
          // Add footer
          const pageNumber = doc.internal.getNumberOfPages();
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text(`Page ${pageNumber}`, margin, pageHeight - 10);
          doc.text(`${settings.appName} - Système de Gestion pour Agence de Voyage`, pageWidth / 2, pageHeight - 10, { align: 'center' });
          doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        }
      });
      
      // Save the PDF
      doc.save('catalogue-forfaits.pdf');
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Export PDF réussi',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'export PDF',
        severity: 'error'
      });
    }
  };

  // Function to handle client selection
  const handleClientSelection = (event, newValue) => {
    setFormData({ 
      ...formData, 
      clientsInteresses: newValue 
    });
  };

  // Add new function to handle opening details dialog
  const handleOpenDetailsDialog = (forfait) => {
    // Only open if we have a valid forfait object
    if (forfait) {
      console.log("Opening details for forfait:", forfait);
      
      // Get the latest data from localStorage for all related entities
      const latestPackages = JSON.parse(localStorage.getItem('packagesData') || '[]');
      const latestReservations = JSON.parse(localStorage.getItem('reservationsData') || '[]');
      const latestClients = JSON.parse(localStorage.getItem('clientsData') || '[]');
      const latestSuppliers = JSON.parse(localStorage.getItem('suppliersData') || '[]');
      
      // Find the most up-to-date version of this forfait
      const updatedForfait = latestPackages.find(p => p.id === forfait.id) || forfait;
      
      // Get the full client data for any interested clients
      let interestedClientsData = [];
      
      if (updatedForfait.clientsInteresses && Array.isArray(updatedForfait.clientsInteresses)) {
        console.log("Client interests found:", updatedForfait.clientsInteresses);
        
        // If clientsInteresses contains full client objects
        if (updatedForfait.clientsInteresses.length > 0 && typeof updatedForfait.clientsInteresses[0] === 'object') {
          interestedClientsData = [...updatedForfait.clientsInteresses];
        } 
        // If clientsInteresses contains only client IDs
        else if (updatedForfait.clientsInteresses.length > 0 && (typeof updatedForfait.clientsInteresses[0] === 'number' || typeof updatedForfait.clientsInteresses[0] === 'string')) {
          interestedClientsData = updatedForfait.clientsInteresses.map(clientId => {
            const client = latestClients.find(c => c.id === clientId || c.id === Number(clientId));
            return client || { id: clientId, nom: 'Client', prenom: '' };
          });
        }
      }
      
      // Get any reservations associated with this forfait
      const associatedReservations = latestReservations.filter(res => 
        res.forfaitId === updatedForfait.id || res.forfait === updatedForfait.titre
      );
      
      console.log("Found associated reservations:", associatedReservations);
      console.log("Found interested clients:", interestedClientsData);
      
      // Get supplier information if available
      let supplierInfo = null;
      if (updatedForfait.fournisseurId) {
        supplierInfo = latestSuppliers.find(s => s.id === updatedForfait.fournisseurId);
        console.log("Found supplier info:", supplierInfo);
      }
      
      // Make a clean copy of the forfait data to display with enhanced data
      const cleanForfait = {
        ...updatedForfait,
        // Don't add any default values - only use what's in the actual data
        inclusions: updatedForfait.inclusions && Array.isArray(updatedForfait.inclusions) ? [...updatedForfait.inclusions] : [],
        // Add the full client objects
        clientsInteresses: interestedClientsData,
        // Add any associated reservations
        reservations: associatedReservations,
        // Add supplier information
        supplier: supplierInfo
      };
      
      console.log("Opening details dialog with data:", cleanForfait);
      setForfaitDetails(cleanForfait);
      setDetailsDialogOpen(true);
      handleMenuClose();
    }
  };

  // Add new function to close details dialog
  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setForfaitDetails(null);
  };

  // Add a new function to handle inclusions selection
  const handleInclusionsChange = (event, newValue) => {
    setFormData({
      ...formData,
      inclusions: newValue
    });
  };

  // Common inclusions options for the dropdown
  const inclusionsOptions = [
    'Vol aller-retour',
    'Vol Business',
    'Hébergement',
    'Hôtel 5★',
    'Hôtel 4★',
    'Transferts',
    'Transferts VIP',
    'Guide touristique',
    'Repas complets',
    'Petit-déjeuner',
    'Excursions guidées',
    'Assurance voyage',
    'Transport local',
    'WiFi gratuit'
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Stats */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
          Gestion des Forfaits
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Créez et gérez vos forfaits de voyage avec des destinations attractives
        </Typography>
        
        {/* Real-time Stats Cards */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 3, 
          flexWrap: 'wrap',
          '& .MuiPaper-root': {
            borderRadius: 2,
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: '0 8px 20px -4px rgba(0,0,0,0.1)'
            }
          }
        }}>
          <Paper sx={{ p: 2, flex: 1, minWidth: 150, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ mr: 2, bgcolor: 'primary.main', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TravelIcon sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight="bold">{packageStats.total}</Typography>
              <Typography variant="body2" color="text.secondary">Forfaits Total</Typography>
            </Box>
          </Paper>
          
          <Paper sx={{ p: 2, flex: 1, minWidth: 150, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ mr: 2, bgcolor: 'success.main', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircleIcon sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight="bold">{packageStats.active}</Typography>
              <Typography variant="body2" color="text.secondary">Forfaits Actifs</Typography>
            </Box>
          </Paper>
          
          <Paper sx={{ p: 2, flex: 1, minWidth: 150, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ mr: 2, bgcolor: 'warning.main', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AssignmentIcon sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight="bold">{packageStats.reservations}</Typography>
              <Typography variant="body2" color="text.secondary">Réservations</Typography>
            </Box>
          </Paper>
          
          <Paper sx={{ p: 2, flex: 1, minWidth: 150, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ mr: 2, bgcolor: 'info.main', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PeopleIcon sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight="bold">{packageStats.interestedClients}</Typography>
              <Typography variant="body2" color="text.secondary">Clients Intéressés</Typography>
            </Box>
          </Paper>
        </Box>
        
        {/* Search and Filter Bar */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
          <TextField
            placeholder="Rechercher un forfait..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Catégorie</InputLabel>
            <Select
              value={filterCategorie}
              onChange={(e) => setFilterCategorie(e.target.value)}
              label="Catégorie"
            >
              <MenuItem value="all">Toutes</MenuItem>
              <MenuItem value="Culturel">Culturel</MenuItem>
              <MenuItem value="Aventure">Aventure</MenuItem>
              <MenuItem value="Religieux">Religieux</MenuItem>
              <MenuItem value="Familial">Familial</MenuItem>
              <MenuItem value="Business">Business</MenuItem>
              <MenuItem value="Luxe">Luxe</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Prix</InputLabel>
            <Select
              value={filterPrix}
              onChange={(e) => setFilterPrix(e.target.value)}
              label="Prix"
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="under50k">&lt; 50,000 DZD</MenuItem>
              <MenuItem value="50k-100k">50,000 - 100,000 DZD</MenuItem>
              <MenuItem value="100k-150k">100,000 - 150,000 DZD</MenuItem>
              <MenuItem value="over150k">&gt; 150,000 DZD</MenuItem>
            </Select>
          </FormControl>
          
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={refreshAllData}
            >
              Actualiser
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ExportIcon />}
              onClick={exportForfaitsToPDF}
            >
              Exporter PDF
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Nouveau
            </Button>
          </Box>
        </Box>
      </Box>
        
      {/* Forfaits Grid */}
      {filteredForfaits.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Aucun forfait trouvé
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Essayez de modifier vos filtres ou créez un nouveau forfait
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Créer un forfait
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredForfaits.map((forfait) => (
            <Grid item xs={12} sm={6} md={4} key={forfait.id}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={getPackageImage(forfait)}
                  alt={forfait.titre}
                />
                
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                      {forfait.titre}
                    </Typography>
                    <IconButton 
                      size="small"
                      onClick={(e) => handleMenuClick(e, forfait)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {forfait.destination}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TimeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {forfait.duree}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label={forfait.categorie} 
                      color={getCategorieColor(forfait.categorie)}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    {forfait.statut && (
                      <Chip 
                        label={forfait.statut} 
                        color={getStatutColor(forfait.statut)}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {formatCurrency(forfait.prix)}
                  </Typography>
                  
                  {/* Interconnected Data Display */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Données interconnectées:
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FlightIcon fontSize="small" sx={{ mr: 0.5, color: 'info.main' }} />
                        <Typography variant="body2">
                          {forfait.reservations?.length || 0} réservations
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonPinIcon fontSize="small" sx={{ mr: 0.5, color: 'secondary.main' }} />
                        <Typography variant="body2">
                          {forfait.clientCount || forfait.clients?.length || forfait.clientsInteresses?.length || 0} clients
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Add supplier information */}
                    {forfait.fournisseurId && suppliers && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <BusinessIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
                        <Typography variant="body2">
                          Fournisseur: {
                            (() => {
                              const supplier = suppliers.find(s => s.id === forfait.fournisseurId);
                              return supplier ? supplier.nom : 'Non spécifié';
                            })()
                          }
                        </Typography>
                      </Box>
                    )}
                    
                    {forfait.prochainDepart && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ScheduleIcon fontSize="small" sx={{ mr: 0.5, color: 'success.main' }} />
                        <Typography variant="body2">
                          Prochain départ: {formatDate(forfait.prochainDepart)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
                
                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(forfait)}
                  >
                    Modifier
                  </Button>
                  <Button 
                    size="small" 
                    color="error" 
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteForfait(forfait.id)}
                  >
                    Supprimer
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Forfait Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingForfait ? 'Modifier le forfait' : 'Nouveau forfait'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Titre"
                value={formData.titre}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Destination"
                value={formData.destination}
                onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="categorie-label">Catégorie</InputLabel>
                <Select
                  labelId="categorie-label"
                  value={formData.categorie}
                  label="Catégorie"
                  onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                >
                  <MenuItem value="Culturel">Culturel</MenuItem>
                  <MenuItem value="Business">Business</MenuItem>
                  <MenuItem value="Luxe">Luxe</MenuItem>
                  <MenuItem value="Religieux">Religieux</MenuItem>
                  <MenuItem value="Aventure">Aventure</MenuItem>
                  <MenuItem value="Familial">Familial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prix (DZD)"
                type="number"
                value={formData.prix}
                onChange={(e) => setFormData({ ...formData, prix: Number(e.target.value) })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">DZD</InputAdornment>,
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel id="duree-label">Durée</InputLabel>
                <Select
                  labelId="duree-label"
                  id="duree-select"
                  name="duree"
                  value={formData.duree}
                  onChange={(e) => setFormData({ ...formData, duree: e.target.value })}
                  label="Durée"
                >
                  <MenuItem value="3 jours / 2 nuits">3 jours / 2 nuits</MenuItem>
                  <MenuItem value="4 jours / 3 nuits">4 jours / 3 nuits</MenuItem>
                  <MenuItem value="5 jours / 4 nuits">5 jours / 4 nuits</MenuItem>
                  <MenuItem value="6 jours / 5 nuits">6 jours / 5 nuits</MenuItem>
                  <MenuItem value="7 jours / 6 nuits">7 jours / 6 nuits</MenuItem>
                  <MenuItem value="8 jours / 7 nuits">8 jours / 7 nuits</MenuItem>
                  <MenuItem value="10 jours / 9 nuits">10 jours / 9 nuits</MenuItem>
                  <MenuItem value="14 jours / 13 nuits">14 jours / 13 nuits</MenuItem>
                  <MenuItem value="Weekend (2 jours)">Weekend (2 jours)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="statut-label">Statut</InputLabel>
                <Select
                  labelId="statut-label"
                  value={formData.statut}
                  label="Statut"
                  onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                >
                  <MenuItem value="Actif">Actif</MenuItem>
                  <MenuItem value="Inactif">Inactif</MenuItem>
                  <MenuItem value="Complet">Complet</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Replace URL image field with drag and drop */}
            <Grid item xs={12}>
              <ImageDropzone 
                value={formData.image} 
                onChange={(base64Image) => setFormData({ ...formData, image: base64Image })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={4}
              />
            </Grid>
            
            {/* Inclusions multi-select */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={inclusionsOptions}
                value={formData.inclusions}
                onChange={handleInclusionsChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Inclusions du forfait"
                    placeholder="Sélectionner ce qui est inclus"
                    helperText="Sélectionnez tout ce qui est inclus dans ce forfait"
                  />
                )}
                renderTags={(selectedValues, getTagProps) =>
                  selectedValues.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      variant="outlined"
                      size="small"
                      sx={{ m: 0.5 }}
                    />
                  ))
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
                      {option}
                    </Box>
                  </Box>
                )}
              />
            </Grid>
            
            {/* Remove the clients interested field */}
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Fournisseur</InputLabel>
                <Select
                  label="Fournisseur"
                  value={formData.fournisseurId || ''}
                  onChange={(e) => setFormData({ ...formData, fournisseurId: e.target.value })}
                >
                  <MenuItem value="">Aucun fournisseur</MenuItem>
                  {suppliers && suppliers.filter(s => s.statut === 'Actif').map(supplier => (
                    <MenuItem key={supplier.id} value={supplier.id}>
                      {supplier.nom} ({supplier.type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveForfait}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuListItem onClick={() => handleOpenDetailsDialog(selectedForfait)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Voir détails</ListItemText>
        </MenuListItem>
        <MenuListItem onClick={() => {
          handleOpenDialog(selectedForfait);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Modifier</ListItemText>
        </MenuListItem>
        <Divider />
        <MenuListItem onClick={() => {
          handleDeleteForfait(selectedForfait?.id);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Supprimer</ListItemText>
        </MenuListItem>
      </Menu>
      
      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        {forfaitDetails && (
          <>
            <DialogTitle sx={{ pb: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Détails du Forfait
                </Typography>
                <Chip
                  label={forfaitDetails.statut}
                  color={getStatutColor(forfaitDetails.statut)}
                  sx={{ borderRadius: 2, fontWeight: 600 }}
                />
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                {/* Header Image */}
                <Grid item xs={12}>
                  <Card sx={{ overflow: 'hidden', borderRadius: 2 }}>
                    <CardMedia
                      component="img"
                      height="250"
                      image={getPackageImage(forfaitDetails)}
                      alt={forfaitDetails.titre}
                    />
                  </Card>
                </Grid>
                
                {/* Main Information */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                        {forfaitDetails.titre}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <LocationIcon sx={{ mr: 1, color: 'secondary.main' }} />
                        <Typography variant="h6">
                          {forfaitDetails.destination}
                        </Typography>
                      </Box>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" color="text.secondary">Catégorie</Typography>
                        <Chip
                          label={forfaitDetails.categorie}
                          color={getCategorieColor(forfaitDetails.categorie)}
                          size="small"
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" color="text.secondary">Durée</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TimeIcon sx={{ mr: 0.5, fontSize: 'small', color: 'info.main' }} />
                          <Typography variant="body1" fontWeight={500}>
                            {forfaitDetails.duree}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" color="text.secondary">Statut</Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {forfaitDetails.statut}
                        </Typography>
                      </Box>
                      
                      {forfaitDetails.dateCreation && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1" color="text.secondary">Date de création</Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatDate(forfaitDetails.dateCreation)}
                          </Typography>
                        </Box>
                      )}
                      
                      {forfaitDetails.dateModification && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1" color="text.secondary">Dernière modification</Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatDate(forfaitDetails.dateModification)}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Add supplier information */}
                      {forfaitDetails.supplier && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Fournisseur
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                              {forfaitDetails.supplier.nom?.charAt(0) || 'F'}
                            </Avatar>
                            <Box>
                              <Typography variant="body1" fontWeight={500}>
                                {forfaitDetails.supplier.nom}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {forfaitDetails.supplier.type} • {forfaitDetails.supplier.ville}, {forfaitDetails.supplier.pays}
                              </Typography>
                            </Box>
                            <Chip 
                              label={forfaitDetails.supplier.statut} 
                              color={forfaitDetails.supplier.statut === 'Actif' ? 'success' : 'error'}
                              size="small"
                              sx={{ ml: 'auto' }}
                            />
                          </Box>
                          
                          {forfaitDetails.supplier.telephone && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">Téléphone</Typography>
                              <Typography variant="body2">{forfaitDetails.supplier.telephone}</Typography>
                            </Box>
                          )}
                          
                          {forfaitDetails.supplier.email && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">Email</Typography>
                              <Typography variant="body2">{forfaitDetails.supplier.email}</Typography>
                            </Box>
                          )}
                        </>
                      )}
                      
                      <Divider sx={{ my: 2 }} />
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Price and Booking Info */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%', borderRadius: 2, bgcolor: 'primary.light', color: 'white' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Informations de tarification
                      </Typography>
                      
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'rgba(255,255,255,0.15)', 
                        borderRadius: 2, 
                        mb: 3, 
                        textAlign: 'center'
                      }}>
                        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                          Prix par personne
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                          {formatCurrency(forfaitDetails.prix)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                          {forfaitDetails.prix && settings.taxRate ? `TTC (dont TVA ${settings.taxRate}%)` : 'TTC'}
                        </Typography>
                      </Box>
                      
                      {/* Only show inclusions if they were explicitly added during creation */}
                      {forfaitDetails.inclusions && Array.isArray(forfaitDetails.inclusions) && forfaitDetails.inclusions.length > 0 && (
                        <>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Ce qui est inclus:
                          </Typography>
                          
                          <List dense sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, mb: 2 }}>
                            {forfaitDetails.inclusions.map((item, index) => (
                              <ListItem key={index}>
                                <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                                  <CheckCircleIcon fontSize="small" />
                                </ListItemIcon>
                                <MuiListItemText 
                                  primary={item}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      )}
                      
                      {/* Removing the "Réserver ce forfait" button as it doesn't have functionality */}
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Description */}
                {forfaitDetails.description && (
                  <Grid item xs={12}>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                          Description
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                          {forfaitDetails.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                
                {/* Related Data */}
                <Grid item xs={12}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Données associées
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(99, 102, 241, 0.08)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <FlightIcon sx={{ mr: 1, color: 'info.main' }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Réservations
                              </Typography>
                            </Box>
                            
                            {(forfaitDetails.reservations && forfaitDetails.reservations.length > 0) ? (
                              <List dense>
                                {forfaitDetails.reservations.map((reservation, index) => (
                                  <ListItem key={index}>
                                    <Typography variant="body2">
                                      {reservation.id}: {formatDate(reservation.dateDepart)} - {reservation.clientNom} 
                                      ({reservation.nombrePassagers} passager{reservation.nombrePassagers > 1 ? 's' : ''})
                                    </Typography>
                                  </ListItem>
                                ))}
                              </List>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Aucune réservation associée
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(124, 58, 237, 0.08)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <PersonPinIcon sx={{ mr: 1, color: 'secondary.main' }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Clients intéressés
                              </Typography>
                            </Box>
                            
                            {(forfaitDetails.clientsInteresses && forfaitDetails.clientsInteresses.length > 0) ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {forfaitDetails.clientsInteresses.map((client, index) => (
                                  <Chip 
                                    key={index}
                                    avatar={<Avatar>{client.prenom?.[0] || client.nom?.[0] || 'C'}</Avatar>}
                                    label={`${client.prenom || ''} ${client.nom || 'Client'}`}
                                    size="small"
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Aucun client intéressé
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleCloseDetailsDialog} variant="outlined" sx={{ borderRadius: 2 }}>
                Fermer
              </Button>
              <Button 
                onClick={() => { handleOpenDialog(forfaitDetails); handleCloseDetailsDialog(); }}
                variant="contained" 
                startIcon={<EditIcon />}
                sx={{ borderRadius: 2 }}
              >
                Modifier
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog for Deletion */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={cancelDeleteForfait}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirmation de suppression</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            Êtes-vous sûr de vouloir supprimer ce forfait ? Cette action est irréversible.
            {deleteConfirmDialog.hasReservations && (
              <Box sx={{ mt: 2, color: 'error.main' }}>
                <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                <Typography component="span" fontWeight="bold">
                  Attention: Ce forfait a des réservations associées.
                </Typography>
                <Typography sx={{ mt: 1 }}>
                  En supprimant ce forfait, toutes les réservations associées seront également supprimées.
                  Cette action est irréversible et pourrait affecter les données clients et financières.
                </Typography>
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteForfait} color="primary">
            Annuler
          </Button>
          <Button onClick={confirmDeleteForfait} color="error" variant="contained">
            {deleteConfirmDialog.hasReservations ? 'Supprimer forfait et réservations' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Forfaits; 